import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  Snackbar,
} from "@mui/material";
import {
  Favorite,
  FavoriteBorderOutlined,
  MoreHoriz,
  SendRounded,
  CheckCircle,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import NavbarLogin from "scenes/widgets/navbarlogin";
import { resolveAssetUrl } from "utils/media";
import PostsWidget from "scenes/widgets/PostsWidget";
import PostEditorDialog from "scenes/widgets/PostEditorDialog";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const normalizeComments = (comments = [], prefix = "comment") =>
  Array.isArray(comments)
    ? comments
        .map((comment, index) => {
          if (typeof comment === "string") {
            const text = comment.trim();
            if (!text) return null;
            return {
              _id: `${prefix}-${index}`,
              text,
              firstName: "",
              lastName: "",
              username: "",
              artistName: "",
              userPicturePath: "",
              likes: {},
              replies: [],
              createdAt: null,
            };
          }
          if (!comment || typeof comment !== "object") return null;
          const text = (comment.text || comment.comment || "").trim();
          if (!text) return null;
          return {
            _id: String(comment._id || `${prefix}-${index}`),
            text,
            firstName: comment.firstName || "",
            lastName: comment.lastName || "",
            username: comment.username || "",
            artistName: comment.artistName || "",
            userPicturePath: comment.userPicturePath || "",
            likes: comment.likes && typeof comment.likes === "object" ? comment.likes : {},
            replies: normalizeComments(comment.replies || [], `${prefix}-${index}`),
            createdAt: comment.createdAt || null,
          };
        })
        .filter(Boolean)
    : [];

const countComments = (comments = []) =>
  comments.reduce(
    (sum, comment) => sum + 1 + countComments(Array.isArray(comment.replies) ? comment.replies : []),
    0
  );

const getCommentLabel = (count) => (count === 1 ? "Kommentar" : "Kommentare");

const formatRelativeTime = (dateInput) => {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";

  const seconds = Math.max(1, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  const years = Math.floor(days / 365);
  return `${years}y`;
};

const WettbewerbPostPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const token = useSelector((state) => state.token);
  const currentUser = useSelector((state) => state.user);
  const [post, setPost] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [postMenuAnchorEl, setPostMenuAnchorEl] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [isCommentSaving, setIsCommentSaving] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState({});
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [isLikeSaving, setIsLikeSaving] = useState(false);

  const images = useMemo(() => {
    if (!post) return [];
    if (Array.isArray(post.imageUrls) && post.imageUrls.length > 0) {
      return post.imageUrls;
    }
    return post.picturePath ? [post.picturePath] : [];
  }, [post]);

  const likeMap = post?.likes || {};
  const canEditPost = Boolean(currentUser?._id === post?.userId || currentUser?.isAdmin);
  const canDeletePost = Boolean(currentUser?._id === post?.userId || currentUser?.isAdmin);
  const isLiked = currentUser?._id ? Boolean(likeMap[currentUser._id]) : false;
  const likeCount = Object.keys(likeMap).length;
  const commentTree = useMemo(() => normalizeComments(post?.comments || []), [post?.comments]);
  const commentCount = useMemo(() => countComments(commentTree), [commentTree]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const showUiMessage = (message, severity = "info") => {
    setSnackbarState({
      open: true,
      message,
      severity,
    });
  };

  const loadPost = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/posts/post/${postId}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Post konnte nicht geladen werden.");
      }
      setPost(data);
    } catch (error) {
      setPost({ error: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPost();
  }, [postId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeIndex < images.length) return;
    setActiveIndex(images.length > 0 ? images.length - 1 : 0);
  }, [activeIndex, images.length]);

  const patchLike = async () => {
    if (!token || !currentUser?._id || !post?._id) {
      navigate("/einloggen");
      return;
    }
    if (isLikeSaving) return;

    const previousPost = post;
    const currentLikes = post.likes || {};
    const nextLikes = { ...currentLikes };
    if (nextLikes[currentUser._id]) {
      delete nextLikes[currentUser._id];
    } else {
      nextLikes[currentUser._id] = true;
    }

    setPost((prevPost) => {
      if (!prevPost) return prevPost;
      return { ...prevPost, likes: nextLikes };
    });
    setIsLikeSaving(true);

    try {
      const response = await fetch(`${BASE_URL}/posts/${post._id}/like`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const updatedPost = await response.json();
      if (!response.ok) {
        throw new Error(updatedPost?.message || "Like konnte nicht gespeichert werden.");
      }
      setPost(updatedPost);
    } catch (error) {
      setPost(previousPost);
      showUiMessage(error.message || "Like konnte nicht gespeichert werden.", "error");
    } finally {
      setIsLikeSaving(false);
    }
  };

  const createComment = async () => {
    if (!token || !currentUser?._id || !post?._id) {
      navigate("/einloggen");
      return;
    }
    const text = commentText.trim();
    if (!text || isCommentSaving) return;

    setIsCommentSaving(true);
    try {
      const response = await fetch(`${BASE_URL}/posts/${post._id}/comments`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text }),
      });
      const updatedPost = await response.json();
      if (!response.ok) {
        throw new Error(updatedPost.message || "Kommentar konnte nicht gespeichert werden.");
      }
      setPost(updatedPost);
      setCommentText("");
    } catch (error) {
      showUiMessage(error.message || "Kommentar konnte nicht gespeichert werden.", "error");
    } finally {
      setIsCommentSaving(false);
    }
  };

  const createReply = async (commentId) => {
    if (!token || !currentUser?._id || !post?._id) {
      navigate("/einloggen");
      return;
    }
    const text = replyText.trim();
    if (!text || isCommentSaving) return;

    setIsCommentSaving(true);
    try {
      const response = await fetch(`${BASE_URL}/posts/${post._id}/comments/${commentId}/replies`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text }),
      });
      const updatedPost = await response.json();
      if (!response.ok) {
        throw new Error(updatedPost.message || "Antwort konnte nicht gespeichert werden.");
      }
      setPost(updatedPost);
      setReplyText("");
      setReplyingToId(null);
    } catch (error) {
      showUiMessage(error.message || "Antwort konnte nicht gespeichert werden.", "error");
    } finally {
      setIsCommentSaving(false);
    }
  };

  const updateComment = async (commentId) => {
    if (!token || !currentUser?._id || !post?._id) {
      navigate("/einloggen");
      return;
    }
    const text = editingText.trim();
    if (!text || isCommentSaving) return;

    setIsCommentSaving(true);
    try {
      const response = await fetch(`${BASE_URL}/posts/${post._id}/comments/${commentId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text }),
      });
      const updatedPost = await response.json();
      if (!response.ok) {
        throw new Error(updatedPost.message || "Kommentar konnte nicht bearbeitet werden.");
      }
      setPost(updatedPost);
      setEditingCommentId(null);
      setEditingText("");
    } catch (error) {
      showUiMessage(error.message || "Kommentar konnte nicht bearbeitet werden.", "error");
    } finally {
      setIsCommentSaving(false);
    }
  };

  const removeComment = async (commentId) => {
    if (!token || !currentUser?._id || !post?._id) {
      navigate("/einloggen");
      return;
    }
    const shouldDelete = window.confirm("Kommentar wirklich löschen?");
    if (!shouldDelete || isCommentSaving) return;

    setIsCommentSaving(true);
    try {
      const response = await fetch(`${BASE_URL}/posts/${post._id}/comments/${commentId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const updatedPost = await response.json();
      if (!response.ok) {
        throw new Error(updatedPost.message || "Kommentar konnte nicht gelöscht werden.");
      }
      setPost(updatedPost);
      if (replyingToId === commentId) {
        setReplyingToId(null);
        setReplyText("");
      }
      if (editingCommentId === commentId) {
        setEditingCommentId(null);
        setEditingText("");
      }
    } catch (error) {
      showUiMessage(error.message || "Kommentar konnte nicht gelöscht werden.", "error");
    } finally {
      setIsCommentSaving(false);
    }
  };

  const toggleCommentLike = async (commentId) => {
    if (!token || !currentUser?._id || !post?._id) {
      navigate("/einloggen");
      return;
    }
    try {
      const response = await fetch(`${BASE_URL}/posts/${post._id}/comments/${commentId}/like`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const updatedPost = await response.json();
      if (!response.ok) {
        throw new Error(updatedPost.message || "Kommentar-Like konnte nicht gespeichert werden.");
      }
      setPost(updatedPost);
    } catch (error) {
      showUiMessage(error.message || "Kommentar-Like konnte nicht gespeichert werden.", "error");
    }
  };

  const renderComments = (comments, depth = 0) =>
    comments.map((comment) => {
      const commentLikeMap = comment.likes || {};
      const commentLikeCount = Object.keys(commentLikeMap).length;
      const isCommentLiked = currentUser?._id ? Boolean(commentLikeMap[currentUser._id]) : false;
      const isOwnComment = Boolean(currentUser?._id && comment.userId === currentUser._id);
      const commentDisplayName = comment.artistName || (comment.username ? `@${comment.username}` : "Nutzer");
      const replies = Array.isArray(comment.replies) ? comment.replies : [];
      const hasHiddenReplies = replies.length > 2;
      const isExpanded = Boolean(expandedReplies[comment._id]);
      const visibleReplies = isExpanded ? replies : replies.slice(0, 2);
      const isEditing = editingCommentId === comment._id;

      return (
        <Box key={comment._id} sx={{ ml: depth > 0 ? 3 : 0, mt: 1.25 }}>
          <Box display="flex" gap={1} alignItems="flex-start">
            <Avatar src={resolveAssetUrl(comment.userPicturePath)} sx={{ width: 30, height: 30 }} />
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                <Typography fontSize="0.85rem" fontWeight={600}>
                  {commentDisplayName}
                </Typography>
                {comment.createdAt && (
                  <Typography fontSize="0.75rem" color="text.secondary">
                    {formatRelativeTime(comment.createdAt)}
                  </Typography>
                )}
                {isOwnComment && (
                  <>
                    <Button
                      size="small"
                      onClick={() => {
                        setEditingCommentId(comment._id);
                        setEditingText(comment.text);
                        setReplyingToId(null);
                      }}
                      sx={{ minWidth: "auto", px: 0, textTransform: "none", fontSize: "0.75rem" }}
                    >
                      Bearbeiten
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => removeComment(comment._id)}
                      sx={{ minWidth: "auto", px: 0, textTransform: "none", fontSize: "0.75rem" }}
                    >
                      Löschen
                    </Button>
                  </>
                )}
              </Box>
              {isEditing ? (
                <Box display="flex" gap={1} mt={0.5}>
                  <TextField
                    value={editingText}
                    onChange={(event) => setEditingText(event.target.value)}
                    size="small"
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={() => updateComment(comment._id)}
                    disabled={isCommentSaving || !editingText.trim()}
                  >
                    Speichern
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingCommentId(null);
                      setEditingText("");
                    }}
                  >
                    Abbrechen
                  </Button>
                </Box>
              ) : (
                <Typography fontSize="0.9rem">{comment.text}</Typography>
              )}
              <Box display="flex" gap={1} alignItems="center" mt={0.25}>
                <Button
                  size="small"
                  onClick={() => {
                    setReplyingToId(comment._id);
                    setReplyText("");
                    setEditingCommentId(null);
                  }}
                  sx={{ minWidth: "auto", px: 0, textTransform: "none" }}
                >
                  Antworten
                </Button>
                <IconButton
                  size="small"
                  onClick={() => toggleCommentLike(comment._id)}
                  sx={{ p: 0.3 }}
                >
                  {isCommentLiked ? (
                    <Favorite color="error" fontSize="small" />
                  ) : (
                    <FavoriteBorderOutlined fontSize="small" />
                  )}
                </IconButton>
                {commentLikeCount > 0 && (
                  <Typography fontSize="0.75rem" color="text.secondary">
                    {commentLikeCount}
                  </Typography>
                )}
              </Box>
              {replyingToId === comment._id && (
                <Box display="flex" gap={1} mt={1}>
                  <TextField
                    value={replyText}
                    onChange={(event) => setReplyText(event.target.value)}
                    placeholder="Antwort schreiben ..."
                    size="small"
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={() => createReply(comment._id)}
                    disabled={isCommentSaving || !replyText.trim()}
                  >
                    Senden
                  </Button>
                  <Button
                    onClick={() => {
                      setReplyingToId(null);
                      setReplyText("");
                    }}
                  >
                    Abbrechen
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
          {hasHiddenReplies && !isExpanded && (
            <Button
              size="small"
              onClick={() =>
                setExpandedReplies((prev) => ({
                  ...prev,
                  [comment._id]: true,
                }))
              }
              sx={{ ml: depth > 0 ? 4 : 5, mt: 0.25, minWidth: "auto", px: 0, textTransform: "none" }}
            >
              Alle {replies.length} Antworten anzeigen
            </Button>
          )}
          {visibleReplies.length > 0 && renderComments(visibleReplies, depth + 1)}
          {hasHiddenReplies && isExpanded && (
            <Button
              size="small"
              onClick={() =>
                setExpandedReplies((prev) => ({
                  ...prev,
                  [comment._id]: false,
                }))
              }
              sx={{ ml: depth > 0 ? 4 : 5, mt: 0.25, minWidth: "auto", px: 0, textTransform: "none" }}
            >
              Antworten ausblenden
            </Button>
          )}
        </Box>
      );
    });

  const deletePost = async () => {
    if (!token || !post?._id || !canDeletePost) return;

    const response = await fetch(`${BASE_URL}/posts/${post._id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      setIsDeleteDialogOpen(false);
      navigate("/wettbewerb");
    }
    setPostMenuAnchorEl(null);
  };

  const handleShare = async () => {
    const postLink = `${window.location.origin}/wettbewerb/post/${postId}`;
    try {
      await navigator.clipboard.writeText(postLink);
      showUiMessage("Link wurde in die Zwischenablage kopiert.", "success");
    } catch (error) {
      showUiMessage(postLink, "info");
    } finally {
      setPostMenuAnchorEl(null);
    }
  };

  const handleEditSaved = (updatedPost) => {
    setPost(updatedPost);
    setActiveIndex(0);
  };

  return (
    <Box>
      <NavbarLogin />
      <Box maxWidth="1100px" mx="auto" px={2} pb={4}>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && post?.error && (
          <Typography color="error">{post.error}</Typography>
        )}
        {!isLoading && post && !post.error && (
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "2fr 1fr" }}
            gap={2}
            borderRadius="12px"
            overflow="hidden"
            bgcolor="background.alt"
          >
            <Box bgcolor="black">
              {images[activeIndex] && (
                <Box
                  component="img"
                  src={resolveAssetUrl(images[activeIndex])}
                  alt="post"
                  sx={{ width: "100%", maxHeight: "75vh", objectFit: "contain" }}
                />
              )}
              <Box display="flex" gap={1} p={1} justifyContent="center">
                {images.map((image, idx) => (
                  <Box
                    key={`${image}-${idx}`}
                    component="img"
                    src={resolveAssetUrl(image)}
                    alt={`thumb-${idx}`}
                    onClick={() => setActiveIndex(idx)}
                    sx={{
                      width: 58,
                      height: 58,
                      objectFit: "cover",
                      cursor: "pointer",
                      border: idx === activeIndex ? "2px solid #fff" : "2px solid transparent",
                      borderRadius: "8px",
                    }}
                  />
                ))}
              </Box>
            </Box>
            <Box p={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                  <Avatar src={resolveAssetUrl(post.userPicturePath)} />
                  <Box>
                    <Typography fontWeight={600}>
                      <Box
                        component={RouterLink}
                        to={post.username ? `/u/${post.username}` : `/profile/${post.userId}`}
                        sx={{
                          display: "inline",
                          lineHeight: "inherit",
                          color: "inherit",
                          textDecoration: "none",
                          "&:visited, &:active": {
                            color: "inherit",
                            textDecoration: "none",
                          },
                          "&:hover": {
                            color: "inherit",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {post.artistName || `@${post.username || "profil"}`}
                      </Box>
                      {post.isOfficial && (
                        <Tooltip
                          title="Verifiziert"
                          slotProps={{
                            tooltip: {
                              sx: {
                                borderRadius: "999px",
                              },
                            },
                          }}
                        >
                          <CheckCircle
                            sx={{
                              ml: 0.5,
                              fontSize: "0.95rem",
                              color: "text.secondary",
                              verticalAlign: "middle",
                            }}
                          />
                        </Tooltip>
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <Box
                        component={RouterLink}
                        to={post.username ? `/u/${post.username}` : `/profile/${post.userId}`}
                        sx={{
                          display: "inline",
                          lineHeight: "inherit",
                          color: "text.secondary",
                          textDecoration: "none",
                          "&:visited, &:active": {
                            color: "text.secondary",
                            textDecoration: "none",
                          },
                          "&:hover": {
                            color: "text.secondary",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        @{post.username || "profil"}
                      </Box>
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={(event) => setPostMenuAnchorEl(event.currentTarget)}>
                  <MoreHoriz />
                </IconButton>
                <Menu
                  anchorEl={postMenuAnchorEl}
                  open={Boolean(postMenuAnchorEl)}
                  onClose={() => setPostMenuAnchorEl(null)}
                  disableScrollLock
                >
                  <MenuItem onClick={handleShare}>Teilen</MenuItem>
                  {canEditPost && (
                    <MenuItem
                      onClick={() => {
                        setIsEditDialogOpen(true);
                        setPostMenuAnchorEl(null);
                      }}
                    >
                      Bearbeiten
                    </MenuItem>
                  )}
                  {canDeletePost && (
                    <MenuItem
                      onClick={() => {
                        setIsDeleteDialogOpen(true);
                        setPostMenuAnchorEl(null);
                      }}
                    >
                      Löschen
                    </MenuItem>
                  )}
                </Menu>
              </Box>
              <Typography mt={2}>{post.caption || post.description || ""}</Typography>
              <Box mt={2} display="flex" alignItems="center" gap={1}>
                <IconButton onClick={patchLike} disabled={!token || isLikeSaving}>
                  {isLiked ? <Favorite color="error" /> : <FavoriteBorderOutlined />}
                </IconButton>
                <Typography>{likeCount}</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography fontWeight={600} mb={1}>
                {getCommentLabel(commentCount)} ({commentCount})
              </Typography>
              <Box sx={{ maxHeight: "45vh", overflowY: "auto", pr: 0.5 }}>
                {commentTree.length > 0 ? (
                  renderComments(commentTree)
                ) : (
                  <Typography color="text.secondary" fontSize="0.9rem">
                    Noch keine Kommentare.
                  </Typography>
                )}
              </Box>
              <Box display="flex" gap={1} mt={2}>
                <TextField
                  value={commentText}
                  onChange={(event) => setCommentText(event.target.value)}
                  placeholder="Kommentar hinzufügen ..."
                  size="small"
                  fullWidth
                />
                <IconButton
                  onClick={createComment}
                  disabled={isCommentSaving || !commentText.trim()}
                  color="primary"
                  sx={{
                    alignSelf: "center",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <SendRounded />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}
        {!isLoading && post && !post.error && (
          <PostEditorDialog
            open={isEditDialogOpen}
            onClose={() => setIsEditDialogOpen(false)}
            token={token}
            postId={post._id}
            initialCaption={post.caption || post.description || ""}
            initialSchool={post.school || ""}
            initialAuthorSchool={post.authorSchool || ""}
            initialImageUrls={images}
            onSaved={handleEditSaved}
          />
        )}
        {!isLoading && post && !post.error && (
          <Dialog
            open={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 700 }}>Beitrag löschen?</DialogTitle>
            <DialogContent>
              <Typography color="text.secondary">
                Dieser Beitrag wird dauerhaft gelöscht und kann nicht wiederhergestellt werden.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setIsDeleteDialogOpen(false)}>Abbrechen</Button>
              <Button color="error" variant="contained" onClick={deletePost}>
                Löschen
              </Button>
            </DialogActions>
          </Dialog>
        )}
        {!isLoading && post && !post.error && (
          <Box mt={3}>
            <Typography variant="h5" mb={2}>
              Mehr Kaleidoskope
            </Typography>
            <PostsWidget sort="date" refreshKey={postId} excludePostId={postId} />
          </Box>
        )}
        <Snackbar
          open={snackbarState.open}
          autoHideDuration={4000}
          onClose={() =>
            setSnackbarState((prev) => ({
              ...prev,
              open: false,
            }))
          }
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() =>
              setSnackbarState((prev) => ({
                ...prev,
                open: false,
              }))
            }
            severity={snackbarState.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarState.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default WettbewerbPostPage;
